import React from "react";

export default (props) => {
  return (
    <div>
      <p>
        <b>Portfolio Factor</b>, also called your <b>Privacy Factor</b>, is the
        single number you obtain by dividing your analysis period starting
        portfolio value by 100. This factor is used to standardize all your
        remaining analysis period monthly portfolio values, portfolio transfers,
        and management fees, by dividing them by your privacy factor. The
        purpose is to keep your portfolio wealth and performance private and
        unkown to RoboPPA™. You may enter your actual portfolio data and
        RoboPPA™ will automatically change them to standardized data values
        without retaining the original true values. Your private standardized
        report contains standardized $ values and % values from the performance
        analysis. You can obtain the true $ results by simply multiplying the
        report's standardized $ values by your portfolio factor.
      </p>
      <p>
        <b>Portfolio Value</b> is the sum of the market values of the Home
        Country and Foreign sub-portfolios, where the Home Country value is in
        its local currency. These values include securities total market value
        plus cash and are typically found in the Asset Summaries sections of
        your monthly investment report and are the market closing values for the
        month. If your report does not convert the Foreign side of your
        portfolio to the Home Country currency, you must convert by multiplying
        by the exchange rates for selling the foreign currencies. The exchange
        rate should be from the same date as the reports closing value date. One
        source of exchange rates by dates is in{" "}
        <a href="www.oanda.com/currency/converter">
          www.oanda.com/currency/converter
        </a>
        . Note that RoboPPA™ cannot start the analysis period at a $0 true value
        because the Portfolio Factor would be 0. You must start your analysis
        period when the portfolio has a positive closing value.
      </p>
      <p>
        <b>Transfers</b> are the contributions and withdrawals that you made to
        your portfolio during the analysis period. These may be in the form of
        cash or security transfers from a separate account outside of your
        portfolio account. Transactions inside the portfolio, such as asset
        purchases and sales, dividend and interest payments, stock splits and
        stock dividends, etc. affect your cash balance but are NOT transfers. If
        your portfolio is a combination of more than one portfolio, transfers
        between those portfolios are NOT transfers when analyzing the combined
        portfolio. Attributing the combined portfolio results to the component
        portfolios is not possible; separate analyses must be done for each
        portfolio. Transfers are typically found in the 'Account Activity'
        section of your investment report.
      </p>
      <p>
        <b>Management Fees</b> are amounts charged by an external portfolio
        manager and paid either directly from the portfolio or directly out of
        pocket from the owner/investor. It is critical that you do not enter any
        brokerage fees or other transaction fees as management fees. Owner
        managed portfolios will have zero management fees.
      </p>
      <p>
        <b>Foreign Exchange (FX) Rate</b>is the market price of one country's
        currency relative to another country's currency. It is imperative that
        the Portfolio's standardized values entered in Robo PPA™ are in a single
        currency, typically but not necesarily, the Portfolio's home country
        currency. In other words, the Portfolio's foreign assets must have
        values in the single currency. Failure to do so will result in an
        incorrect analysis.
      </p>
      <p>
        <b>Annualized Return</b> is the portfolio's monthly return value
        commonly expressed as an annual number. This is similar to interest
        rates that are expressed as an annualized number but calculated monthly.
        In some reports, RoboPPA™ compounds up a monthly return to an effective
        annual number rather than simply multiplying by 12. The latter is termed
        an annual nominal return and is less than the effective return.
      </p>
      <p>
        <b>Internal Rate of Return (IRR)</b>, also called{" "}
        <b>Dollar Weighted Return</b>, is the discount rate that equates the
        discounted value of cash outflows with the discounted value of cash
        inflows, where cash flows occur within the analysis time period and are
        discounted to the start of the analysis period. In the RoboPPA™
        application, the cash flows are the initial value of the portfolio
        (inflow), the cash contributions (inflows) and withdrawals (outflows),
        and the portfolio's terminal value (outflow). RoboPPA™ annualizes the
        analysis period's IRR.
      </p>
      <p>
        <b>Time Weighted Return (TWR)</b>, also called the{" "}
        <b>Holding Period Return (HPR)</b> is the return earned by any initial
        portfolio investment, ignoring the size and timing of intermediate cash
        transfers. This return is computed by calculating the Portfolio's
        terminal value from a $1 investment and no additional contributions,
        which equals the product of 1s plus the Portfolio's monthly returns,
        over the analysis period. It is only one measure of the manager's
        performance because it does not differentiate between the differing
        owner contributions. Robo PPA™ annualizes the analysis period TWR. The
        time weighted return is generally less than the annualized monthly
        average return and equals the IRR only when there are no portfolio
        transfers.
      </p>
      <p>
        <b>Active Monthly Return</b> is the return from Active Management
        defined as the difference between the portfolio's monthly return and
        your benchmark's monthly return. The proportion of months the Active
        Return is positive indicates the probability that the Portfolio returns
        more than the Benchmark, in any month.
      </p>
      <p>
        <b>Average Return</b> is the arithmetic average of a Portfolio's monthly
        returns, over the analysis period.
      </p>
      <p>
        <b>Excess Average Return</b> is the difference between a Portfolio's
        average return and the average return on cash (e.g., on short term
        Teasury bills).
      </p>
      <p>
        <b>Return Volatility</b> is a measure of the total risk of a Portfolio's
        monthly return and is measured by the standard deviation of the monthly
        return, calculated over the analysis period. With normally distributed
        returns, 95% of the returns will lie between the average plus 1.96
        standard deviations and the average minus 1.96 standard deviations. More
        volatile portfolios are considered to be more risky, in part because the
        probability of negative returns increases with the size of the standard
        deviation.
      </p>
      <p>
        <b>Beta</b> is the sensitivity of (change in) a Portfolio’s monthly
        return to a unit change in the return on a benchmark portfolio, which in
        RoboPPA™ is a stock market index. Large beta portfolios are said to be
        aggressive because of they magnify market movements. Defensive
        portfolios have betas close to zero.
      </p>
      <p>
        <b>5% Value at Risk (5%VaR)</b> is a measure of the downside risk of a
        Portfolio's monthly return. There is a 5% probability that the monthly
        return will be less than the calculated 5%VaR value. Equivalently, there
        is a 95% probability that the loss will not be worse than the 5%VaR
        return value. More negative values are considered more risky. RoboPPA™
        calculates the number from the portfolio's analysis period distribution.
      </p>
      <p>
        <b>Expected Shortfall (ES</b>) is a measure of downside risk of a
        Portfolio's non-annualized monthly return. In RoboPPA™, it is the
        portfolio's average monthly return if a monthly return is negative. More
        negative values are considered more risky. ES is less than the
        portfolio's average return and RoboPPA™ calculates the number from the
        portfolio's analysis period distribution.
      </p>
      <p>
        <b>Sharpe Ratio</b>, also called the reward to volatility ratio, is a
        measure of a Portfolio's excess average return per unit of risk
        performance. It is calculated as the ratio of the portfolio's average
        return in excess of the average riskless return, divided by the
        portfolio's volatility. Riskless return is measured by the return on
        Federal Government Treasury Bills. RoboPPA™ calculates the number from
        the portfolio's observed analysis period returns and adjusts it
        downwards for fewer months in the analysis period.
      </p>
      <p>
        <b>Market Timing</b> is a portfolio manager's ability to magnify the
        portfolio’s positive returns in bull markets and dampen the negative
        returns in bear markets.
      </p>
      <p>
        <b>Amortized Loss or Gain</b> is a loss or gain value over the analysis
        period that is distributed across the months in the analysis period and
        considers the time value of money, using a specifed interest
        amortization rate.
      </p>
      <p>
        <b>Bond Yield</b> is the promised return on a bond if held to maturity
        and is typically reported as the annual nominal rate of interest under
        semi-annual compounding. This implies that the true or effective yield
        is larger. RoboPPA™ uses 10-year Federal Government Bond yields as an
        economic factor potentially affecting a portfolio's performance.
      </p>
      <p>
        <b>Portfolio Style</b> measures the similarity of a Portfolio's return
        properties to a simple rebalanced portfolio formed from a number of
        benchmarks or “style” portfolios. RoboPPA™ style portfolios may include
        Gov’t Treasury bills, a home country stock index, value and growth stock
        indexes, and the unhedged S&amp;P500 stock index. A simple equally
        weighted style portfolio's return and risk is compared to your
        Portfolio's return and risk.
      </p>
      <p>
        <b>Statistical Significance</b> is a measure of a sample statistic's
        (e.g., average return) difference from zero. This is particularly
        important in drawing conclusions about a portfolio's return/risk
        performance when your submitted analysis period (sample size) is small.
        RoboPPA™ only measures the significance of your portfolio's beta and
        your portfolio's exposure to specified economic factors; however, it
        issues a general caution warning, applicable to all sample statistics,
        when the analysis period is short.
      </p>
      <p>
        <b>Benchmarks</b> are market traded portfolios that are useful for
        comparisons with your Portfolio. They represent a purchaseable
        alternative to your Portfolio that may have superior performance after
        their fees, which are typically lower than normal management fees. Robo
        PPA™ uses your specified portfolio of Benchmarks for direct comparison
        with your Portfolio and also allows you to select three additional
        benchmarks for comparisons.
      </p>
      <p>
        <b>Economic Factor Exposure</b> measures the sensitivity of your
        portfolio's monthly returns to changes in the values of some economic
        factors. This sensitivity can be important for investors wanting to
        limit their exposure to factor risks such as changes in inflation, short
        and medium term interest rates, foreign currency exchange rates, and
        general stock market return and risk.
      </p>
      <p>
        <b>Manager Value Added</b> is initially determined from the analysis
        period's difference between your Portfolio's and Benchmark's after fees
        terminal (ending) values; this value added does not separate out value
        due to risk taking. The value added is refined to jointly consider the
        return and risk by using a derivative valuation of the Portfolio's
        return components and a certainty equivalent valuation of the return
        distribution.
      </p>
      <p>
        <b>Management Fee</b> recommendations in Robo PPA™ are based on a
        manager having fees such that the after fees, risk-adjusted, value-added
        is non-negative.
      </p>
      <p>
        <b>Manager Termination</b> recommendations in Robo PPA™ are based on
        your manager's after fees actual value added to your specified
        Benchmark, the analysis period length, and your portfolio's after fees
        average excess return per unit of volatility (Sharpe Ratio).
        Specifically, if your manager's added value after fees is negative and
        if your Benchmark has a larger Sharpe ratio, you should consider
        replacing the manager with another manager or your Benchmark. If a
        simple style portfolio, constructed by combining the benchmark ETFs, has
        better after fees risk-return characteristics than your Portfolio and
        your Benchmark, then it could be considered as a replacement for your
        managed Portfolio or Benchmark. However, the decision should also
        consider the unreliability of a short analysis period and the costs of
        transitioning to a new portfolio.
      </p>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/portfolio/components/Definitions.js